import React from 'react';
import clsx from 'clsx';
import { AImage } from '../../atoms/AImage';
import { AScrimGradient } from '../../atoms/AScrimGradient';
import { AScrimGradientDirection } from '../../atoms/AScrimGradient/AScrimGradient.types';
import { MTextBlockFull } from '../../molecules/MTextBlockFull';
import { MButton, MButtonType } from '../../molecules/MButton';
import { MMediaStack } from '../../molecules/MMediaStack';
import {
  OEditorialHighlightProps,
  OEditorialHighlightOverlay,
  OEditorialHighlightContentAlignment,
} from './OEditorialHighlight.types';
import { MMediaStackImageLayout } from '../../molecules/MMediaStack/MMediaStack.types';
import { enrichMMediaStackForEditorialHighlight } from './OEditorialHighlight.utils';
import * as Styled from './OEditorialHighlight.styles';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OEditorialHighlight';
const TEST_ID = 'mi-web-ui-kit-OEditorialHighlight';

export const OEditorialHighlight: React.FC<OEditorialHighlightProps> = ({
  overlay = OEditorialHighlightOverlay.Dark,
  imageLayout = MMediaStackImageLayout.Default,
  contentAlignment = OEditorialHighlightContentAlignment.Centered,
  container,
  aImage,
  aScrimGradient,
  mTextBlockFull,
  mButton,
  mMediaStack,
  trackingProperties,
  className,
  styleclass,
  ...rest
}) => {
  const showOverlay = overlay !== OEditorialHighlightOverlay.None;

  // Scrim direction: image section always has darker background than content section.
  // Default = content left / media right → gradient flows LeftToRight.
  // Reverse = media left / content right → gradient flows RightToLeft.
  const scrimDirection =
    imageLayout === MMediaStackImageLayout.Default
      ? AScrimGradientDirection.LeftToRight
      : AScrimGradientDirection.RightToLeft;

  const resolvedMediaStack = mMediaStack ? enrichMMediaStackForEditorialHighlight(mMediaStack) : undefined;

  return (
    <Styled.Container
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(className, 'm-container-fullbleed', overlay === OEditorialHighlightOverlay.None && styleclass)}
      $container={container}
      {...rest}
    >
      {/* Background image: only for Dark/Light overlays; None uses brand background color */}
      {aImage && showOverlay && (
        <Styled.BackgroundMedia>
          <AImage {...aImage} />
        </Styled.BackgroundMedia>
      )}
      {/* Scrim: rendered for Dark/Light when background image is present;
               direction auto-derived from imageLayout so media side is always darker */}
      {showOverlay && aImage && (
        <Styled.ScrimGradientWrapper $overlay={overlay} $imageLayout={imageLayout}>
          <AScrimGradient {...(aScrimGradient ?? {})} direction={scrimDirection} />
        </Styled.ScrimGradientWrapper>
      )}
      <Styled.Wrapper $imageLayout={imageLayout}>
        <Styled.ContentSection
          $contentAlignment={contentAlignment}
          $imageLayout={imageLayout}
          className={`${overlay === OEditorialHighlightOverlay.Dark ? 'color-scheme2' : ''}`}
        >
          <Styled.ContentWrapper $contentAlignment={contentAlignment}>
            {mTextBlockFull && (
              <Styled.TextBlockWrapper $contentAlignment={contentAlignment}>
                <MTextBlockFull {...mTextBlockFull} />
              </Styled.TextBlockWrapper>
            )}
            {mButton?.buttonText && (
              <Styled.ButtonWrapper $contentAlignment={contentAlignment} $overlay={overlay}>
                <MButton
                  {...mButton}
                  buttonType={mButton?.type as MButtonType}
                  trackingProperties={trackingProperties}
                />
              </Styled.ButtonWrapper>
            )}
          </Styled.ContentWrapper>
        </Styled.ContentSection>
        {resolvedMediaStack && (
          <Styled.MediaSection
            $imageLayout={imageLayout}
            $mediaStackAspectRatio={resolvedMediaStack.aspectRatio}
            $mediaStackImageDirection={resolvedMediaStack.imageDirection}
          >
            <MMediaStack {...resolvedMediaStack} imageLayout={imageLayout} />
          </Styled.MediaSection>
        )}
      </Styled.Wrapper>
    </Styled.Container>
  );
};
